import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OauthConfigService } from '@stg-js-ngx-core/oauth';
import { AppConfigService } from '../appServiceConfig';
import { MmtCommonService } from '../common/services/mmt-common.service';

/**
 * HTTP interceptor that:
 * 1. Prepends the correct API base URL from appsettings for relative API paths
 * 2. Attaches the Bearer token to all outgoing API requests
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private _commonService!: MmtCommonService;

  constructor(
    private oauthConfigService: OauthConfigService,
    private appConfigService: AppConfigService,
    private injector: Injector
  ) {}

  private get commonService(): MmtCommonService {
    if (!this._commonService) {
      this._commonService = this.injector.get(MmtCommonService);
    }
    return this._commonService;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldSkip(req.url)) {
      return next.handle(req);
    }

    const config = this.appConfigService.getConfig();
    const url = this.resolveUrl(req.url, config);

    const token = this.oauthConfigService.oauthToken;
    let headers = req.headers;
    if (token && !headers.has('Authorization')) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    // Attach the active facility's cost center as the 'hospital' header.
    // Priority: facility chosen from the Select Facility modal (hospitalChange) wins over
    // the hierarchy dropdown so picking a facility doesn't have to mutate hierarchy state.
    if (!headers.has('hospital')) {
      const hospitalHeader = this.resolveHospitalHeader();
      if (hospitalHeader) {
        headers = headers.set('hospital', hospitalHeader);
      }
    }

    const clonedReq = req.clone({ url, headers });

    // Library's role-switch call expects ADAMS profile directly, but our API wraps in {data: ...}
    if (this.isRoleProfileCall(req.url)) {
      return next.handle(clonedReq).pipe(
        map((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse && event.body && event.body.data) {
            const profile = event.body.data;
            // Library's setDefaultSelectedTeam() expects teams array; ADAMS profile may not include it
            if (!profile.teams) {
              profile.teams = [];
            }
            return event.clone({ body: profile });
          }
          return event;
        })
      );
    }

    if (this.isChildrenCall(req.url)) {
      return next.handle(clonedReq).pipe(
        map((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse && event.body && event.body.data) {
            return event.clone({ body: event.body.data });
          }
          return event;
        })
      );
    }

    return next.handle(clonedReq);
  }

  private isRoleProfileCall(url: string): boolean {
    const clean = url.replace(/^\/+/, '').replace(/^ui\//, '');
    return clean.startsWith('api/users/profiles');
  }

  private isChildrenCall(url: string): boolean {
    const clean = url.replace(/^\/+/, '').replace(/^ui\//, '');
    return clean.startsWith('api/hierarchy/') && clean.includes('/children/');
  }

  private resolveHospitalHeader(): string | null {
    const hospital: any = this.commonService.hospitalChange?.getValue?.();
    if (hospital && hospital.costCenter) {
      return String(hospital.costCenter);
    }
    return this.extractTeamName(this.commonService.hierarchy);
  }

  private extractTeamName(value: any): string | null {
    if (value == null) {
      return null;
    }
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.length ? this.extractTeamName(value[0]) : null;
    }
    if (typeof value === 'object') {
      return value.team_name || value.teamName || value.costCenter || null;
    }
    return null;
  }

  private resolveUrl(url: string, config: any): string {
    if (!config || url.startsWith('http')) {
      return url;
    }
    // Library URLs arrive rootContext-prefixed as "//api/..." or "/ui/api/..."; normalise both
    // so the rewrites below match, otherwise they fall through to the generic base-url branch.
    const cleanUrl = url.replace(/^\/+/, '').replace(/^ui\//, '');
    // Library's RbacService calls /api/users/profiles?roleName=...&appName=... for role switching
    if (cleanUrl.startsWith('api/users/profiles')) {
      const queryString = cleanUrl.includes('?') ? cleanUrl.split('?')[1] : '';
      const params = new URLSearchParams(queryString);
      const roleName = params.get('roleName') || '';
      return config.api.mmt + '/User/GetCustomRbacProfile?roleName=' + encodeURIComponent(roleName);
    }
    if (cleanUrl.startsWith('api/users/applications/') && cleanUrl.includes('searchHierarchy_with_rbac')) {
      const parsed = this.parseSearchUrl(cleanUrl);
      return config.api.mmt + '/Adams/HierarchySearch?application=' +
        encodeURIComponent(parsed.application) + '&role=' + encodeURIComponent(parsed.role) +
        '&limit=' + parsed.limit + '&user_name=' + encodeURIComponent(parsed.user_name) +
        '&search=' + encodeURIComponent(parsed.search);
    }
    if (cleanUrl.startsWith('api/hierarchy/')) {
      const parsed = this.parseChildrenUrl(cleanUrl);
      return config.api.mmt + '/Adams/HierarchyChildren?teamName=' +
        encodeURIComponent(parsed.teamName) + '&sourceSystemId=' + encodeURIComponent(parsed.sourceSystemId) +
        (parsed.search ? '&search=' + encodeURIComponent(parsed.search) : '') +
        (parsed.liveOnApplication ? '&liveOnApplication=' + encodeURIComponent(parsed.liveOnApplication) : '');
    }
    return this.resolveBaseUrl(cleanUrl, config) + '/' + cleanUrl;
  }

  /**
   * Parse hierarchy search URL from the library:
   * api/users/applications/{app}/roles/{role}/searchHierarchy_with_rbac/hierarchy_tree?limit=&user_name=&search=
   */
  private parseSearchUrl(url: string): any {
    // Extract path segments
    const pathAndQuery = url.split('?');
    const pathParts = pathAndQuery[0].split('/');
    // api/users/applications/{app}/roles/{role}/searchHierarchy_with_rbac/hierarchy_tree
    //  0    1       2          3      4     5            6                      7
    const application = pathParts[3];
    const role = decodeURIComponent(pathParts[5]);

    // Parse query params
    const params = new URLSearchParams(pathAndQuery[1] || '');
    return {
      application,
      role,
      limit: params.get('limit') || '50',
      user_name: params.get('user_name') || '',
      search: params.get('search') || ''
    };
  }

  /**
   * Parse hierarchy children URL from the library:
   * api/hierarchy/{teamName}/children/{sourceSystemId}?search=&liveOnApplication=
   */
  private parseChildrenUrl(url: string): any {
    const pathAndQuery = url.split('?');
    const pathParts = pathAndQuery[0].split('/');
    // api/hierarchy/{teamName}/children/{sourceSystemId}
    //  0      1         2         3           4
    const teamName = pathParts[2];
    const sourceSystemId = pathParts[4];

    const params = new URLSearchParams(pathAndQuery[1] || '');
    return {
      teamName,
      sourceSystemId,
      search: params.get('search') || '',
      liveOnApplication: params.get('liveOnApplication') || ''
    };
  }

  private shouldSkip(url: string): boolean {
    // Don't intercept static asset requests or appsettings
    if (url.startsWith('/assets/') || url === '/appsettings.json') {
      return true;
    }
    // Don't intercept already-absolute URLs (OAuth Lambda calls, external)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return true;
    }
    return false;
  }

  private resolveBaseUrl(url: string, config: any): string {
    // ADAMS endpoints are proxied through .NET API's AdamsController
    if (url.startsWith('/Adams/')) {
      return config.api.mmt;
    }
    // AAIMS endpoints are proxied through .NET API's AaimsController
    if (url.startsWith('/Aaims/')) {
      return config.api.mmt;
    }
    // Config endpoints served by .NET API
    if (url.startsWith('/Config/')) {
      return config.api.mmt;
    }
    // All other API endpoints go to MMT .NET API
    return config.api.mmt;
  }
}
